import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition, faSync } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/auth.service";
import "../../styles/app/DashboardCard.css";

interface DashboardCardProps {
    icon: IconDefinition;
    title: string;
    description: string;
    actions?: React.ReactNode;
    className?: string;
    showVerificationAlerts?: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ icon, title, description, actions, className = "", showVerificationAlerts = false }) => {
    const { user, customerData } = useAuth();
    const navigate = useNavigate();
    const isVerify = localStorage.getItem("isVerify") === "true";

    const [resending, setResending] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [resendMessage, setResendMessage] = useState<string | null>(null);
    const [resendError, setResendError] = useState<string | null>(null);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const handleResend = async () => {
        const email = customerData?.Email || user?.email;
        if (!email) return;
        setResending(true);
        setResendMessage(null);
        setResendError(null);

        const result = await authService.resendVerificationEmail(email);
        setResending(false);

        if (result.success) {
            setResendMessage('¡Correo de verificación reenviado con éxito!');
            setCountdown(60);
        } else {
            setResendError(result.error || 'Ocurrió un error al reenviar el correo.');
        }
    };

    return (
        <>
            <div className={`dashboard-card ${className}`}>
                <div className="dashboard-card-main-content">
                    <FontAwesomeIcon icon={icon} className="dashboard-card-icon" />
                    <div className="dashboard-card-text">
                        <h2>{title}</h2>
                        <p>{description}</p>
                    </div>
                </div>
                {actions && (
                    <div className="dashboard-card-actions">
                        {actions}
                    </div>
                )}
            </div>

            {showVerificationAlerts && customerData && !customerData.isVerify && !isVerify && (
                <div className="verification-warning">
                    <div className="verification-warning-content">
                        <p>
                            Tu cuenta aún no ha sido verificada. Por favor, revisa tu correo
                            electrónico para completar la verificación.{" "}
                            <span className="verification-resend-link-wrapper">
                                ¿No lo recibiste?{" "}
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    className="verification-resend-link"
                                    disabled={resending || countdown > 0}
                                >
                                    {resending ? 'Reenviando...' : countdown > 0 ? `Reenviar en ${countdown}s` : 'Reenviar correo'}
                                    <FontAwesomeIcon icon={faSync} className={resending ? 'fa-spin' : ''} style={{ marginLeft: '5px' }} />
                                </button>
                            </span>
                        </p>
                    </div>
                    {resendMessage && (
                        <div className="verification-resend-success">
                            {resendMessage}
                        </div>
                    )}
                    {resendError && (
                        <div className="verification-resend-error">
                            {resendError}
                        </div>
                    )}
                </div>
            )}

            {showVerificationAlerts && customerData && !customerData.isComplete && (
                <div className="verification-warning incomplete-profile-alert">
                    <p>
                        ¡Importante! Faltan datos por completar en tu perfil. Por favor, haz
                        clic{" "}
                        <span
                            onClick={() => navigate("/customer/profile")}
                            style={{
                                fontWeight: "bold",
                                textDecoration: "underline",
                                cursor: "pointer",
                            }}
                        >
                            aquí
                        </span>{" "}
                        para ir a tu perfil y completarlos.
                    </p>
                </div>
            )}
        </>
    );
};

export default DashboardCard;
